import { createFileRoute } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useSearchStatus,
  useSearchAnalytics,
  useConnectSearch,
  useDisconnectSearch,
  isTauri,
  onWindowFocus,
} from '@wordpuppi/shared';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { SiteHealthCard } from '@/components/SiteHealthCard';
import { DataForSeoKey } from '@/components/DataForSeoKey';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

export const Route = createFileRoute('/sites/$siteSlug/search')({
  component: SearchPage,
});

function SearchPage() {
  const { siteSlug } = Route.useParams();
  const { data: statusRes, isLoading } = useSearchStatus(siteSlug);
  const status = statusRes?.data;
  const connect = useConnectSearch(siteSlug);
  const disconnect = useDisconnectSearch(siteSlug);
  // The OAuth callback bounces back here with ?gsc_error=... on failure.
  const callbackError = new URLSearchParams(window.location.search).get('gsc_error');

  // AB#490 desktop: consent runs in the system browser (Google blocks embedded
  // webviews), so the webview never reloads. Re-poll connection status when the
  // user tabs back, until `connected` flips. Web reloads on the callback redirect
  // and doesn't need this. Note: the prod Google OAuth client/redirect config is
  // Rick-gated (QA/staging client is configured per AB#297).
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!isTauri() || status?.connected) return;
    return onWindowFocus(() => {
      queryClient.invalidateQueries({ queryKey: ['sites', siteSlug, 'search'] });
    });
  }, [status?.connected, siteSlug, queryClient]);

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Search</h1>
          <p className="text-sm text-muted-foreground mt-1">
            How your site is doing in Google and AI assistants, and what to do next.
          </p>
        </div>
        {status?.connected && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => disconnect.mutate()}
            disabled={disconnect.isPending}
          >
            Disconnect
          </Button>
        )}
      </div>
      {/* AB#1033: shown in every Search Console state, above the GSC content. */}
      <SiteHealthCard siteSlug={siteSlug} />
      <DataForSeoKey siteSlug={siteSlug} />
      {isLoading ? (
        <Skeleton className="h-96 w-full" />
      ) : status?.connected ? (
        <AnalyticsTable siteSlug={siteSlug} />
      ) : (
        <ConnectCard
          error={callbackError}
          pending={connect.isPending}
          // No OAuth client on the server → `connect` returns { url: undefined }.
          notConfigured={!!connect.data && !connect.data.data?.url}
          onConnect={() => connect.mutate()}
        />
      )}
    </div>
  );
}

function ConnectCard({ error, pending, notConfigured, onConnect }: {
  error: string | null;
  pending: boolean;
  notConfigured: boolean;
  onConnect: () => void;
}) {
  return (
    <Card id="search-console">
      <CardContent className="py-12 text-center">
        {error && (
          <p className="text-destructive text-sm mb-4">
            Google connection failed: {error}. Please try again.
          </p>
        )}
        <p className="text-muted-foreground mb-4">
          Authorize WordPuppi to read this site's Search Console data.
        </p>
        {/* Outline: the health card above owns the screen's one amber button (Desk Lamp Rule). */}
        <Button variant="outline" onClick={onConnect} disabled={pending}>
          Connect Google Search Console
        </Button>
        {notConfigured && (
          <p className="text-muted-foreground text-sm mt-4">
            Search Console isn't configured on the server yet. Please try again later.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function AnalyticsTable({ siteSlug }: { siteSlug: string }) {
  const { data: analyticsRes, isLoading } = useSearchAnalytics(siteSlug);
  const data = analyticsRes?.data;

  if (isLoading) return <Skeleton className="h-48 w-full" />;

  if (!data?.rows.length) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          {data?.error ? (
            <>
              <p className="text-destructive mb-2">
                Couldn't fetch Search Console data ({data.error}).
              </p>
              {data.detail && <p className="text-xs">{data.detail}</p>}
            </>
          ) : (
            <>
              No search data yet. Google Search Console can take a few days to report
              impressions for a new site.
            </>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="rounded-lg border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Page</TableHead>
            <TableHead className="text-right">Clicks</TableHead>
            <TableHead className="text-right">Impressions</TableHead>
            <TableHead className="text-right">CTR</TableHead>
            <TableHead className="text-right">Position</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.rows.map((row) => (
            <TableRow key={row.page}>
              <TableCell className="font-medium">{row.page}</TableCell>
              <TableCell className="text-right">{row.clicks.toLocaleString()}</TableCell>
              <TableCell className="text-right">
                {row.impressions.toLocaleString()}
              </TableCell>
              <TableCell className="text-right">{(row.ctr * 100).toFixed(1)}%</TableCell>
              <TableCell className="text-right">{row.position.toFixed(1)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
